// src/componets/dashboard/pages-dashboard/UserOrders.jsx
import React, { useState } from "react";
import { Badge, Button } from "@radix-ui/themes";
import {
  RefreshCw,
  ExternalLink,
  AlertTriangle,
  ShoppingCart,
  CreditCard,
  ChevronDown,
  ChevronRight,
  Package,
  MapPin,
  Calendar,
  BarChart3,
  ChevronUp,
  Ticket,
  Receipt,
} from "lucide-react";
import { useUserOrders } from "../../../hooks/useUserOrders";
import LoadingScreen from "../../ui/LoadingScreen";

// Componente para cuando no hay órdenes
const EmptyOrdersView = React.memo(() => {
  return (
    <div className="min-h-[50vh] flex flex-col justify-center items-center text-center">
      <div className="max-w-md mx-auto">
        <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto ">
          <ShoppingCart className="w-8 h-8 text-blue-600" />
        </div>
        <h2 className="text-xl font-medium text-gray-900 mb-2">
          No tienes pedidos aún
        </h2>
        <p className="text-gray-600 mb-6">
          Cuando realices tu primer pedido, aparecerá aquí con toda la
          información.
        </p>
        <Button asChild size="3" className="bg-blue-600 hover:bg-blue-700">
          <a href="/nuevo-pedido" className="text-white no-underline">
            Hacer mi primer pedido
          </a>
        </Button>
      </div>
    </div>
  );
});

EmptyOrdersView.displayName = "EmptyOrdersView";

// Componente de cabecera con estadísticas
// const OrdersHeader = React.memo(({ stats, isRefreshing, onRefresh }) => {
//   return (
//     <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-6 mb-4 border border-blue-100">
//       <div className="flex lg:items-center items-center justify-between gap-6">
//         {/* Estadísticas */}

//         <div className="flex items-center gap-6">
//           <div className="text-center">
//             <h1 className="text-lg font-medium text-gray-900 mb-0 pb-0">
//               {stats.total}
//             </h1>
//             <div className="text-base text-gray-600">Tus pedidos</div>
//           </div>

//           {stats.pendingAmount > 0 && (
//             <div className="text-center">
//               <div className="text-lg font-semibold text-red-600">
//                 {stats.pendingAmountFormatted}
//               </div>
//               <div className="text-base text-gray-600">Por pagar</div>
//             </div>
//           )}
//         </div>

//         {/* Acciones */}
//         <div className="flex gap-3">
//           <Button
//             variant="outline"
//             onClick={onRefresh}
//             disabled={isRefreshing}
//             size="3"
//           >
//             <RefreshCw
//               className={`w-4 h-4 ${isRefreshing ? "animate-spin" : ""}`}
//             />
//           </Button>
//         </div>
//       </div>
//     </div>
//   );
// });

// OrdersHeader.displayName = "OrdersHeader";

// Componente para mostrar detalles expandidos de la orden
const OrderDetailsExpanded = React.memo(({ order, getShortOrderId }) => {
  return (
    <div className="space-y-4">
      {/* Detalles de cada item */}
      <div className="space-y-3">
        {order.order_details.map((detail, index) => (
          <div key={detail.id} className="bg-gray-50 rounded-2xl p-4 flex ">
            {/* 
              <Badge color="blue" variant="soft">
                {detail.status}
              </Badge> */}
            <img
              src="/assets/img/jugs-states/nobg-new-100.png"
              alt=""
              className="h-40 hidden md:block"
            />
            <div className=" space-y-4 w-full ">
              <div className="flex gap-2">
                {"Orden de servicio: "}
                <div className="text-base font-medium text-gray-800 p-2 py-0.5 bg-gray-200 rounded-xl max-w-fit ">
                  {detail.short_id
                    ? detail.short_id.toUpperCase()
                    : `DET-${detail.id.slice(-6)}`}
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <div className="text-gray-600 gap-2 flex flex-col md:flex-row md:items-center text-base">
                  <div className="flex gap-2 items-center">
                    <MapPin className="min-w-4 min-h-4 w-4 h-4 text-gray-500" />
                    Entrega:
                  </div>
                  <div className="text-gray-900 flex items-start gap-1">
                    <span>{detail.delivery_address}</span>
                  </div>
                </div>
                {detail.actual_delivery_formatted && (
                  <div className="text-gray-600 gap-2 flex items-center text-base">
                    <div className="flex gap-2 items-center">
                      <Calendar className="min-w-4 min-h-4 w-4 h-4 text-gray-500" />
                      Fecha de entrega:
                    </div>
                    <div className="text-gray-900 flex items-start gap-1">
                      {detail.actual_delivery_formatted}
                    </div>
                  </div>
                )}
              </div>
              <hr />
              <div className="flex flex-col md:flex-row justify-between gap-4 text-base">
                {/* Producto */}
                <div>
                  <span className="font-medium text-gray-800">Producto</span>
                  {/* <div className="text-gray-900">{detail.product_name}</div> */}
                  <div className="text-gray-900 flex gap-2">
                    Botellon 20 lt
                    <Badge
                      color={detail.detail_type === "new" ? "green" : "blue"}
                      variant="soft"
                    >
                      {detail.detail_type === "new" ? "Nuevo" : "Recambio"}
                    </Badge>
                  </div>
                </div>
                {detail.delivered_jug_barcode && (
                  <div>
                    <span className="font-medium text-green-600">
                      Botellón asignado
                    </span>
                    <div className=" text-gray-900">
                      {detail.delivered_jug_barcode}
                    </div>
                  </div>
                )}
                {detail.returned_jug_barcode && (
                  <div>
                    <span className="font-medium text-orange-600">
                      Botellón retornado
                    </span>
                    <div className=" text-gray-900">
                      {detail.returned_jug_barcode}
                    </div>
                  </div>
                )}
                {/* Cantidad y precio */}
                <div>
                  <span className="font-medium text-gray-800">Cantidad</span>
                  <div className="text-gray-900 flex gap-2">
                    {detail.quantity}x{detail.unit_price_formatted} c/u
                  </div>
                  <div className="text-gray-600"></div>
                </div>
                {/* Total del detalle */}
                <div>
                  <span className="font-medium text-gray-800">Subtotal</span>
                  <div className="text-gray-900 font-medium">
                    {detail.detail_total_formatted}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Información de pagos si hay */}
      {order.payments && order.payments.length > 0 && (
        <div>
          <span className="font-medium text-gray-700 mb-2 block">
            Historial de pagos:
          </span>
          <div className="space-y-2">
            {order.payments.map((payment, index) => (
              <div
                key={index}
                className="bg-white rounded-lg p-3 border border-gray-200 text-base"
              >
                <div className="flex justify-between items-center">
                  <div>
                    <span className="font-medium">
                      {new Intl.NumberFormat("es-CO", {
                        style: "currency",
                        currency: "COP",
                        minimumFractionDigits: 0,
                      }).format(payment.amount)}
                    </span>
                    <span className="text-gray-600 ml-2">
                      {new Date(payment.payment_date).toLocaleDateString(
                        "es-CO"
                      )}
                    </span>
                  </div>
                  <Badge
                    color={
                      payment.payment_status === "paid" ? "green" : "orange"
                    }
                    variant="soft"
                  >
                    {payment.payment_status}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
});

OrderDetailsExpanded.displayName = "OrderDetailsExpanded";

// Componente de card de orden
const OrderCard = React.memo(
  ({
    order,
    onPayment,
    isExpanded,
    onToggleExpand,
    getShortOrderId,
    getOrderStatusColor,
    getPaymentStatusColor,
    getOrderStatusLabel,
    getPaymentStatusLabel,
  }) => {
    return (
      <div className="bg-white rounded-2xl hover:shadow-md transition-shadow">
        {/* Header del card */}
        <div className="p-6 space-y-4">
          {/* Primera fila: Número de orden y botón expandir */}
          <div className="flex flex-col md:flex-row gap-4 md:items-center justify-between">
            <div className="flex gap-2 items-center">
              <div className="p-2 bg-blue-50 rounded-lg">
                <Ticket className="w-5 h-5 text-blue-600" />
              </div>
              Pedido
              <div
                className={`text-base font-medium p-2 py-0.5 ${
                  order.payment_status === "pending"
                    ? "bg-orange-100 text-orange-700 "
                    : "bg-gray-100 text-gray-800 "
                } rounded-xl max-w-fit`}
              >
                {getShortOrderId(order)}
              </div>
            </div>
            <div className="flex gap-2 items-center">
              <Badge
                size={"3"}
                color={getOrderStatusColor(order.order_status)}
                variant="soft"
              >
                {getOrderStatusLabel(order.order_status)}
              </Badge>
              <Badge
                size={"3"}
                color={getPaymentStatusColor(order.payment_status)}
                variant="soft"
              >
                {getPaymentStatusLabel(order.payment_status)}
              </Badge>
              {order.needs_payment && !order.is_incomplete && (
                <Button
                  size="3"
                  onClick={() => onPayment(order.id)}
                  className="bg-green-600 hover:bg-green-700 text-white hidden md:inline-flex"
                >
                  Finalizar pago
                </Button>
              )}
            </div>
          </div>
          <hr />

          {/* Segunda fila: Información básica */}
          <div className="flex flex-col md:flex-row md:justify-between gap-1 md:gap-4">
            {/* Fecha */}
            <div>
              <div className="text-base text-gray-500 mb-1">Fecha</div>
              <div className="text-base font-medium text-gray-900">
                {order.order_date_formatted} {order.order_time_formatted}
              </div>
            </div>

            {/* Items */}
            <div>
              <div className="text-base text-gray-500 mb-1">Items</div>
              {Object.keys(order.products_summary).length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {Object.entries(order.products_summary).map(
                    ([type, count]) => (
                      <Badge key={type} variant="soft" size="3">
                        {count} {type}
                      </Badge>
                    )
                  )}
                </div>
              )}
            </div>

            {/* Productos */}
            <div>
              <div className="text-base text-gray-500 mb-1">Productos</div>
              <div className="text-base font-medium text-gray-900">
                {order.subtotal_formatted}
              </div>
            </div>

            <div>
              <div className="text-base text-gray-500 mb-1">Envío</div>
              <div className="font-medium text-blue-600 text-base">
                {order.has_shipping ? order.shipping_cost_formatted : "Gratis"}
              </div>
            </div>
            {/* Total */}
            <div>
              <div className="text-base text-gray-500 mb-1">Total</div>
              <div className="text-base font-medium text-gray-900">
                {order.order_total_formatted}
              </div>
            </div>

            <div className="flex flex-col gap-2 justify-center items-center pt-4 md:pt-0">
              {order.needs_payment && !order.is_incomplete && (
                <button
                  size="3"
                  onClick={() => onPayment(order.id)}
                  className="p-2 bg-green-600 text-base min-w-12 w-full md:w-12 h-12 hover:bg-green-200 text-white rounded-lg font-medium transition-colors flex items-center justify-center md:hidden"
                >
                  Finalizar pago
                </button>
              )}
              <button
                onClick={onToggleExpand}
                className="p-2 bg-gray-100 text-base min-w-12 w-full md:w-12 h-12 hover:bg-gray-200 rounded-lg transition-colors flex items-center justify-center"
              >
                <span className="md:hidden pe-2">Detalles de tu pedido </span>
                {isExpanded ? (
                  <ChevronUp className="w-5 h-5 text-gray-500" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-gray-500" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Área expandida con detalles */}
        {isExpanded && (
          <div className="px-6 pb-6">
            <OrderDetailsExpanded
              order={order}
              getShortOrderId={getShortOrderId}
            />
          </div>
        )}
      </div>
    );
  }
);

OrderCard.displayName = "OrderCard";

// Componente principal
const UserOrders = () => {
  const {
    orders,
    stats,
    isLoading,
    isRefreshing,
    error,
    refreshOrders,
    handlePayment,
    getShortOrderId,
    getOrderStatusColor,
    getPaymentStatusColor,
    getOrderStatusLabel,
    getPaymentStatusLabel,
    hasOrders,
  } = useUserOrders();

  // Estado para manejar qué cards están expandidos
  const [expandedOrders, setExpandedOrders] = useState(new Set());

  // Función para togglear el estado expandido
  const toggleExpanded = (orderId) => {
    setExpandedOrders((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(orderId)) {
        newSet.delete(orderId);
      } else {
        newSet.add(orderId);
      }
      return newSet;
    });
  };

  // Mostrar pantalla de carga
  if (isLoading) {
    return <LoadingScreen message="Cargando tus pedidos..." />;
  }

  // Mostrar error si existe
  if (error) {
    return (
      <div className="min-h-[50vh] flex flex-col items-center justify-center text-center">
        <div className="max-w-md mx-auto">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto ">
            <AlertTriangle className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-xl font-medium text-gray-900 mb-2">
            Error al cargar
          </h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <Button onClick={refreshOrders} disabled={isRefreshing} size="3">
            <RefreshCw
              className={`w-4 h-4 mr-2 ${isRefreshing ? "animate-spin" : ""}`}
            />
            Reintentar
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="py-2 md:px-0">
      {!hasOrders ? (
        <EmptyOrdersView />
      ) : (
        <div className="space-y-6">
          {/* Header con estadísticas */}
          {/* <OrdersHeader
            stats={stats}
            isRefreshing={isRefreshing}
            onRefresh={refreshOrders}
          /> */}

          {/* Grid de cards de órdenes */}
          <div className="space-y-4">
            {orders.map((order) => (
              <OrderCard
                key={order.id}
                order={order}
                onPayment={handlePayment}
                isExpanded={expandedOrders.has(order.id)}
                onToggleExpand={() => toggleExpanded(order.id)}
                getShortOrderId={getShortOrderId}
                getOrderStatusColor={getOrderStatusColor}
                getPaymentStatusColor={getPaymentStatusColor}
                getOrderStatusLabel={getOrderStatusLabel}
                getPaymentStatusLabel={getPaymentStatusLabel}
              />
            ))}
          </div>

          {/* Footer informativo */}
          <div className="space-y-3">
            {stats.pending > 0 && (
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <div className="flex items-center gap-2 text-orange-800">
                  <AlertTriangle className="w-5 h-5" />
                  <span className="font-medium">
                    Tienes {stats.pending} pedido{stats.pending > 1 ? "s" : ""}{" "}
                    pendiente{stats.pending > 1 ? "s" : ""} de pago
                  </span>
                </div>
              </div>
            )}

            {/* Información sobre códigos de barras */}
            {orders.some((o) => o.barcodes_involved.length > 0) && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center gap-2 text-blue-800">
                  <BarChart3 className="w-5 h-5" />
                  <span className="text-base">
                    <strong>Tip:</strong> Expande los detalles de cada pedido
                    para ver los códigos de barras de los botellones
                    involucrados y más información.
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default UserOrders;
